import { Editor } from "@tiptap/core";
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import Box from "../../Box";
import Flex from "../../Flex";
import styled from "styled-components";
import PopupMenu from "../../PopupMenu/PopupMenu";
import PopupItem from "../../PopupMenu/Item";
import useBoolean from "../../../hooks/useBoolean";
import Icon from "../../Icon";
import { getColor, getIconFill } from "../../utils";
import Text from "../../Text/Text";
import useEditorToolbarLayout from "../hooks/useEditorToolbarLayout";
import { getToolbarGroups } from "../utils/toolbarGroups";
import HighlightColorOptions from "./HighlightColorOptions";
import TextColorOptions from "./TextColorOptions";

const ToolbarButton = styled.button<{ textButton?: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${props => (props.textButton ? "95px" : "32px")};
  height: 32px;
  padding: 8px;
  border-radius: 4px;
  &.is-active {
    background-color: ${getColor("grayLighter")};
  }
  &:hover {
    background-color: ${getColor("grayLighter")};
  }
`;

const ToolbarContainer = styled(Box)<{
  sticky?: boolean;
}>`
  position: ${props => (props.sticky ? "sticky" : "absolute")};
  top: ${props => (props.sticky ? "var(--editor-toolbar-offset, 0px)" : "-55px")};
  display: flex;
  width: fit-content;
  gap: 8px;
  box-shadow:
    0px 0.33px 1.3px rgba(0, 0, 0, 0.03),
    0px 1.54px 5.1px rgba(0, 0, 0, 0.04),
    0px 4px 16px rgba(0, 0, 0, 0.07);
  background: white;
  border-radius: 4px;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  height: 48px;
  font-size: 16px;
  z-index: 9;
`;

const Separator = styled(Box).attrs({
  background: "grayLighter",
  width: "1px",
  radius: "32px",
  height: "24px",
})``;

type ToolbarButtonConfig = {
  action: () => void;
  iconId: string;
  activeCondition: string;
  disabledCondition?: boolean;
  onPopupClick?: () => void;
  popupSource?: string;
  renderButton?: () => React.ReactNode;
};

export type ToolbarGroup = {
  id: string;
  buttons: ToolbarButtonConfig[];
};

const ColorIndicator = styled.div<{ indicatorColor: string }>`
  position: absolute;
  bottom: 2px;
  left: 6px;
  right: 6px;
  height: 3px;
  border-radius: 1px;
  background-color: ${props => props.indicatorColor};
`;

type Props = {
  editor: Editor;
  source: string;
  setEditingLabel: React.Dispatch<React.SetStateAction<boolean>>;
  backgroundColorMenu: React.ReactNode;
  onInlineColorMenuChange?: (open: boolean) => void;
  refs: {
    textOptionRef: React.RefObject<HTMLDivElement>;
    rootRef: React.RefObject<HTMLDivElement>;
    sentinelRef: React.RefObject<HTMLDivElement>;
    toolbarRef: React.RefObject<HTMLDivElement>;
    colorOptionRef: React.RefObject<HTMLDivElement>;
    overflowOptionRef: React.RefObject<HTMLDivElement>;
  };
  colorProps: {
    colorOptionsShown: boolean;
    showColorOptions: () => void;
    hideColorOptions: () => void;
    buttonBgColor: string;
  };
  onAIGenerate?: () => void;
};

const Toolbar = ({
  editor,
  source,
  setEditingLabel,
  refs,
  backgroundColorMenu,
  onInlineColorMenuChange,
  colorProps,
  onAIGenerate,
}: Props) => {
  const { textOptionRef, rootRef, sentinelRef, toolbarRef, colorOptionRef, overflowOptionRef } =
    refs;
  const { colorOptionsShown, showColorOptions, hideColorOptions, buttonBgColor } = colorProps;
  const [menuShown, showMenu, hideMenu] = useBoolean(false),
    [overflowMenuShown, showOverflowMenu, hideOverflowMenu] = useBoolean(false),
    [highlightMenuShown, showHighlightMenu, hideHighlightMenu] = useBoolean(false),
    [textColorMenuShown, showTextColorMenu, hideTextColorMenu] = useBoolean(false),
    textDropdownSource = useMemo(() => source + "-dropdown", [source]),
    overflowDropdownSource = useMemo(() => source + "-overflow", [source]),
    getBlockLabel = useCallback(() => {
      if (editor.isActive("heading", { level: 1 })) return "Heading 1";
      if (editor.isActive("heading", { level: 2 })) return "Heading 2";
      if (editor.isActive("paragraph")) return "Paragraph";
      return "Paragraph";
    }, [editor]);

  const highlightMenuRef = useRef<HTMLDivElement>(null);
  const textColorMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onInlineColorMenuChange?.(highlightMenuShown || textColorMenuShown);
  }, [highlightMenuShown, textColorMenuShown, onInlineColorMenuChange]);

  const colorGroup: ToolbarGroup = useMemo(() => {
    const highlightSource = source + "-highlight",
      textColorSource = source + "-text-color";
    return {
      id: "colorButtons",
      buttons: [
        {
          iconId: "editor.bucket",
          activeCondition: "textColor",
          action: () => {},
          renderButton: () => {
            const activeTextColor = (editor.getAttributes("textColor")?.color as string) || null;
            return (
              <>
                <ToolbarButton
                  aria-label="text color"
                  onClick={e => {
                    e.stopPropagation();
                    showTextColorMenu();
                  }}
                  data-popup-source={textColorSource}>
                  <Icon size="16px" iconId="editor.text-color" />
                  <ColorIndicator indicatorColor={activeTextColor ?? "black"} />
                </ToolbarButton>
                <PopupMenu
                  open={textColorMenuShown}
                  close={hideTextColorMenu}
                  source={textColorSource}
                  ref={textColorMenuRef}>
                  <TextColorOptions editor={editor} />
                </PopupMenu>
              </>
            );
          },
        },
        {
          iconId: "content.color",
          activeCondition: "highlight",
          action: () => {},
          renderButton: () => {
            const activeHighlight = (editor.getAttributes("highlight")?.color as string) || null;
            return (
              <>
                <ToolbarButton
                  aria-label="highlight"
                  onClick={e => {
                    e.stopPropagation();
                    showHighlightMenu();
                  }}
                  data-popup-source={highlightSource}>
                  <Icon fill="black" size="16px" iconId="editor.highlight" />
                  <ColorIndicator indicatorColor={activeHighlight ?? "white"} />
                </ToolbarButton>
                <PopupMenu
                  open={highlightMenuShown}
                  close={hideHighlightMenu}
                  source={highlightSource}
                  ref={highlightMenuRef}>
                  <HighlightColorOptions editor={editor} />
                </PopupMenu>
              </>
            );
          },
        },
      ],
    };
  }, [
    source,
    editor,
    highlightMenuShown,
    showHighlightMenu,
    hideHighlightMenu,
    textColorMenuShown,
    showTextColorMenu,
    hideTextColorMenu,
  ]);

  const toolbarGroups = useMemo(
    () => [colorGroup, ...getToolbarGroups(editor, setEditingLabel, source)],
    [colorGroup, editor, setEditingLabel, source]
  );

  const [visibleGroups, setVisibleGroups] = useState<ToolbarGroup[]>(toolbarGroups);
  const [overflowGroups, setOverflowGroups] = useState<ToolbarGroup[]>([]);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const sentinelEl = sentinelRef.current;
    if (!sentinelEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { root: null, threshold: 0 }
    );

    observer.observe(sentinelEl);
    return () => {
      observer.disconnect();
    };
  }, [sentinelRef]);

  const SeparatorWrap = () => (
    <Flex alignItems="center" justifyContent="center">
      <Separator />
    </Flex>
  );

  const renderGenerateTextButton = useCallback(() => {
    if (!onAIGenerate) return null;
    return (
      <>
        <ToolbarButton onClick={onAIGenerate} aria-label="Generate with AI">
          <Icon fill="black" size="16px" iconId="action.quick-action" />
        </ToolbarButton>
        <SeparatorWrap />
      </>
    );
  }, [onAIGenerate]);

  const renderColorSelection = useCallback(() => {
    if (!backgroundColorMenu) return null;
    const fill = getIconFill(buttonBgColor);
    return (
      <>
        <ToolbarButton
          onClick={showColorOptions}
          data-popup-source={source + "-color-selection"}
          style={{ backgroundColor: buttonBgColor }}>
          <Icon fill={fill} size="16px" iconId="content.color" />
        </ToolbarButton>
        <PopupMenu
          open={colorOptionsShown}
          close={hideColorOptions}
          source={source + "-color-selection"}
          ref={colorOptionRef}>
          {backgroundColorMenu}
        </PopupMenu>
        <SeparatorWrap />
      </>
    );
  }, [
    source,
    colorOptionsShown,
    showColorOptions,
    hideColorOptions,
    colorOptionRef,
    backgroundColorMenu,
    buttonBgColor,
  ]);

  const renderTextDropdown = useCallback(() => {
    return (
      <>
        <ToolbarButton data-popup-source={textDropdownSource} onClick={showMenu} textButton>
          <Flex alignItems="center" gap="4px" width="95px">
            <Text font="ui.smallBold">{getBlockLabel()}</Text>
            <Icon fill="black" size="12px" iconId="navigation.chevron-down" />
          </Flex>
        </ToolbarButton>
        <PopupMenu
          open={menuShown}
          close={hideMenu}
          source={textDropdownSource}
          ref={textOptionRef}>
          <PopupItem
            onClick={() => editor.chain().focus().setParagraph().run()}
            title="Paragraph"
            checked={editor.isActive("paragraph")}
            aria-label="Paragraph"
          />
          <PopupItem
            onClick={() => editor.chain().focus().setHeading({ level: 1 }).run()}
            title="Heading 1"
            checked={editor.isActive("heading", { level: 1 })}
            aria-label="Heading 1"
          />
          <PopupItem
            onClick={() => editor.chain().focus().setHeading({ level: 2 }).run()}
            title="Heading 2"
            checked={editor.isActive("heading", { level: 2 })}
            aria-label="Heading 2"
          />
        </PopupMenu>
        <SeparatorWrap />
      </>
    );
  }, [menuShown, editor, showMenu, hideMenu, textDropdownSource, textOptionRef, getBlockLabel]);

  const renderMainButtons = useCallback(() => {
    return (
      <>
        {visibleGroups.map((group, groupIndex) => (
          <React.Fragment key={group.id}>
            <Flex gap="8px">
              {group.buttons.map(button =>
                button.renderButton ? (
                  <React.Fragment key={button.iconId}>{button.renderButton()}</React.Fragment>
                ) : (
                  <ToolbarButton
                    aria-label={button.activeCondition}
                    key={button.iconId}
                    onClick={button.action}
                    disabled={button.disabledCondition}
                    className={editor.isActive(button.activeCondition) ? "is-active" : ""}
                    data-popup-source={button?.popupSource}>
                    <Icon fill="black" size="16px" iconId={button.iconId} />
                  </ToolbarButton>
                )
              )}
            </Flex>
            {groupIndex < visibleGroups.length - 1 && <SeparatorWrap />}
          </React.Fragment>
        ))}
      </>
    );
  }, [visibleGroups, editor]);

  const renderOverflowButtons = useCallback(() => {
    if (!overflowGroups.length) return null;
    const flatButtons = overflowGroups.flatMap(group => group.buttons);
    return (
      <>
        <SeparatorWrap />
        <ToolbarButton onClick={showOverflowMenu} data-popup-source={overflowDropdownSource}>
          <Icon fill="black" size="16px" iconId="navigation.overflow" />
        </ToolbarButton>
        <PopupMenu
          open={overflowMenuShown}
          close={hideOverflowMenu}
          source={overflowDropdownSource}
          ref={overflowOptionRef}
          width="fit-content"
          hPos="alignRight">
          <Flex gap="8px" p="s">
            {flatButtons.map(button =>
              button.renderButton ? (
                <React.Fragment key={button.iconId}>{button.renderButton()}</React.Fragment>
              ) : (
                <ToolbarButton
                  aria-label={button.activeCondition}
                  key={button.iconId}
                  onClick={button.action}
                  disabled={button.disabledCondition}
                  className={editor.isActive(button.activeCondition) ? "is-active" : ""}
                  data-popup-source={button?.popupSource}>
                  <Icon fill="black" size="16px" iconId={button.iconId} />
                </ToolbarButton>
              )
            )}
          </Flex>
        </PopupMenu>
      </>
    );
  }, [
    overflowGroups,
    overflowDropdownSource,
    overflowMenuShown,
    hideOverflowMenu,
    editor,
    showOverflowMenu,
    overflowOptionRef,
  ]);

  useEditorToolbarLayout({
    rootRef,
    toolbarGroups,
    setMainButtons: setVisibleGroups,
    setOverflowButtons: setOverflowGroups,
  });

  return (
    <ToolbarContainer
      id="toolbar"
      data-testid="editor-toolbar"
      data-popup-source={source}
      sticky={isSticky}
      ref={toolbarRef}>
      {renderGenerateTextButton()}
      {renderColorSelection()}
      {renderTextDropdown()}
      {renderMainButtons()}
      {renderOverflowButtons()}
    </ToolbarContainer>
  );
};

export default Toolbar;
